import React, { useEffect, useId, useState } from 'react';
import { loadScript } from 'utils';

import { printMissingConfigError, SiteConfigManager } from '@atlas/core';

import StaticBlock from 'common/StaticBlock/StaticBlock';

import MenuFooterItem from './MenuFooterItem';
import ReclameAquiLicense from './ReclameAquiLicense';

const ICON_SIZE = 56;
const STYLE = { minWidth: ICON_SIZE };

const Copyright = () => {
  const reclameNodeId = useId();
  const [licencesState, setLicencesState] = useState([]);
  const [hasError, setHasError] = useState(false);

  const isMainSite = window.SYS_CONFIG.isMain;
  const customSettings = SiteConfigManager.getConfig('config.config.custom');
  const footerType = customSettings?.footer_type;

  const isDefaultFooter = footerType === 'default';
  const isHiddenLicense = (!isMainSite && !isDefaultFooter) || footerType === 'without_license';

  const enableApg = SiteConfigManager.getConfig('config.config.license.enableApg', false);
  const enableXcm = SiteConfigManager.getConfig('config.config.license.enableXcm', false);
  const enableGC = SiteConfigManager.getConfig('config.config.license.enableGC', false);
  const enableAnjouan = SiteConfigManager.getConfig('config.config.license.enableAnjouan', false);
  const xcmId = SiteConfigManager.getConfig('config.config.license.xcmId');
  const xcmDomID = SiteConfigManager.getConfig('config.config.license.xcmDomID');
  const apgId = SiteConfigManager.getConfig('config.config.license.apgId');
  const apgDomId = SiteConfigManager.getConfig('config.config.license.apgDomId');
  const GCId = SiteConfigManager.getConfig('config.config.license.GCId', '');
  const anjouanId = SiteConfigManager.getConfig('config.config.license.anjouanId');
  const anjouanDomID = SiteConfigManager.getConfig('config.config.license.anjouanDomID');

  const queryElementSafely = selector => {
    try {
      return document.querySelector(selector);
    } catch (error) {
      console.error(`Error querying selector ${selector}:`, error);
      return null;
    }
  };

  const updateLicencesSafely = newLicense => {
    try {
      setLicencesState(prevState => [...prevState, newLicense]);
    } catch (error) {
      console.error('Error updating licenses state:', error);
      setHasError(true);
    }
  };

  useEffect(() => {
    try {
      if (enableXcm) {
        if (!xcmId || !xcmDomID) {
          return;
        }

        try {
          const licenseDomElement = queryElementSafely(`#${xcmDomID}`);

          if (!licenseDomElement) {
            updateLicencesSafely(
              <div
                className="e-gaming-logo"
                data-xcm-image-size={ICON_SIZE}
                data-xcm-image-type="basic-small"
                data-xcm-seal-id={xcmId}
                id={xcmDomID}
                key={xcmDomID}
                style={STYLE}
              />,
            );
          } else {
            updateLicencesSafely(
              <div
                className="e-gaming-logo"
                dangerouslySetInnerHTML={{ __html: licenseDomElement.innerHTML }}
                key={xcmDomID}
                style={STYLE}
              />,
            );
          }
        } catch (error) {
          console.error('Error processing XCM license:', error);
        }
      }

      if (enableApg) {
        if (!apgId || !apgDomId) {
          return;
        }

        try {
          const licenseDomElement = queryElementSafely(`#${apgDomId}`);

          if (!licenseDomElement) {
            updateLicencesSafely(
              <div
                className="e-gaming-logo"
                data-apg-image-size={ICON_SIZE}
                data-apg-image-type="basic-small"
                data-apg-seal-id={apgId}
                id={apgDomId}
                key={apgDomId}
                style={STYLE}
              />,
            );
          } else {
            updateLicencesSafely(
              <div
                className="e-gaming-logo"
                dangerouslySetInnerHTML={{ __html: licenseDomElement.innerHTML }}
                key={apgDomId}
                style={STYLE}
              />,
            );
          }
        } catch (error) {
          console.error('Error processing APG license:', error);
        }
      }

      if (enableGC) {
        if (!GCId) {
          return;
        }

        try {
          const isProduction = window.SYS_CONFIG?.env === 'production';

          if (isProduction) {
            const href = `https://licensing.gaming-curacao.com/validator/?lh=${GCId}`;
            updateLicencesSafely(
              <a
                aria-label="Gaming Curacao: Click to Validate"
                href={href}
                key={href}
                rel="noreferrer noopener"
                target="_blank"
              >
                <img
                  alt="Gaming Curacao: Click to Validate"
                  border="0"
                  height="50"
                  onError={e => {
                    console.error('Error loading GC image:', e);
                    // eslint-disable-next-line no-param-reassign
                    e.target.style.display = 'none';
                  }}
                  src="https://licensing.gaming-curacao.com/validator/images/Gaming-Curacao-ClickToVerify.png"
                  width="150"
                />
              </a>,
            );
          } else {
            const src = `https://licensing.gaming-curacao.com/validator/?lh=${GCId}&template=tseal`;
            updateLicencesSafely(
              <iframe
                height="50"
                key={src}
                onError={e => {
                  console.error('Error loading GC iframe:', e);
                  // eslint-disable-next-line no-param-reassign
                  e.target.style.display = 'none';
                }}
                src={src}
                style={{ border: 'none' }}
                width="120"
              />,
            );
          }
        } catch (error) {
          console.error('Error processing GC license:', error);
        }
      }

      if (enableAnjouan) {
        if (!anjouanId || !anjouanDomID) {
          return;
        }

        try {
          const licenseDomElement = queryElementSafely(`#${anjouanDomID}`);

          if (!licenseDomElement) {
            updateLicencesSafely(
              <div
                className="e-gaming-logo"
                data-anj-image-size={ICON_SIZE}
                data-anj-image-type="basic-small"
                data-anj-seal-id={anjouanId}
                id={anjouanDomID}
                key={anjouanDomID}
                style={{
                  ...STYLE,
                  maxWidth: STYLE.minWidth,
                }}
              />,
            );
          } else {
            updateLicencesSafely(
              <div
                className="e-gaming-logo"
                dangerouslySetInnerHTML={{ __html: licenseDomElement.innerHTML }}
                key={anjouanDomID}
                style={{
                  ...STYLE,
                  maxWidth: STYLE.minWidth,
                }}
              />,
            );
          }
        } catch (error) {
          console.error('Error processing Anjouan license:', error);
        }
      }

      try {
        updateLicencesSafely(<ReclameAquiLicense customNodeId={reclameNodeId} key="reclame" />);
      } catch (error) {
        console.error('Error adding ReclameAqui license:', error);
      }
    } catch (error) {
      setHasError(true);
    }
  }, [
    GCId,
    anjouanDomID,
    anjouanId,
    apgDomId,
    apgId,
    enableAnjouan,
    enableApg,
    enableGC,
    enableXcm,
    xcmDomID,
    xcmId,
    reclameNodeId,
  ]);

  useEffect(() => {
    const loadScriptSafely = async (src, name) => {
      try {
        await loadScript(src);
      } catch (error) {
        console.error(`Failed to load ${name} script from ${src}:`, error);
        // Don't set hasError here as script loading failures shouldn't break the component
      }
    };

    const loadAllScripts = async () => {
      try {
        const scriptPromises = [];

        if (enableXcm && xcmId) {
          scriptPromises.push(
            loadScriptSafely(`https://${xcmId}.seals-xcm.certria.com/xcm-seal.js`, 'XCM'),
          );
        }

        if (enableApg && apgId) {
          scriptPromises.push(
            loadScriptSafely(`https://${apgId}.snippet.antillephone.com/apg-seal.js`, 'APG'),
          );
        }

        if (enableAnjouan && anjouanId) {
          scriptPromises.push(
            loadScriptSafely(
              `https://${anjouanId}.snippet.anjouangaming.org/anj-seal.js`,
              'Anjouan',
            ),
          );
        }

        await Promise.allSettled(scriptPromises);
      } catch (error) {
        console.error('Error in script loading effect:', error);
      }
    };

    loadAllScripts();
  }, [anjouanId, apgId, enableAnjouan, enableApg, enableXcm, xcmId]);

  try {
    const licenseValidationChecks = [
      {
        message: 'config.config.license[anjouanId|anjouanDomID]',
        shouldLog: enableAnjouan && (!anjouanId || !anjouanDomID),
      },
      {
        message: 'config.config.license.GCId',
        shouldLog: enableGC && !GCId,
      },
      {
        message: 'config.config.license[apgId|apgDomId]',
        shouldLog: enableApg && (!apgId || !apgDomId),
      },
      {
        message: 'config.config.license[xcmId|xcmDomID]',
        shouldLog: enableXcm && (!xcmId || !xcmDomID),
      },
    ];

    licenseValidationChecks.forEach(({ message, shouldLog }) => {
      if (shouldLog) {
        printMissingConfigError(message);
      }
    });
  } catch (error) {
    console.error('Error in configuration validation:', error);
  }

  if (hasError || isHiddenLicense) {
    return null;
  }

  // Error boundary-like behavior for rendering
  if (hasError) {
    console.error('Copyright component encountered an error, rendering fallback');
    return (
      <MenuFooterItem className="e-gaming-container" title="footer_title_license">
        <div className="e-gaming-text">
          <StaticBlock options={{ source: 'footer-license' }} />
        </div>
      </MenuFooterItem>
    );
  }

  return (
    <MenuFooterItem className="e-gaming-container" title="footer_title_license">
      <div className="e-gaming-logo-container">
        {licencesState.map(license => {
          try {
            return license;
          } catch (error) {
            return null;
          }
        })}
      </div>
      <div className="e-gaming-text">
        <StaticBlock options={{ source: 'footer-license' }} />
      </div>
    </MenuFooterItem>
  );
};

export default Copyright;
